import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { t } from 'i18next';
import { useSnackbar } from 'notistack';
import CalendarPicker from '../util/CalendarPicker';
import ClaimantFields from './ClaimantFields';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { transformToHierarchy } from '../util/tree';
import TreeViewComponent from '../util/TreeViewComponent';
import CategoryTaskPicker from '../timesheetsComp/CategoryTaskPicker';

interface AddIncomeProps {
  setShowModalAdd: (v: boolean) => void;
  refreshIncome: () => void;
  staticDateCompany: any;
  access?: any;
}

export default function AddIncome({ setShowModalAdd, refreshIncome, staticDateCompany, access }: AddIncomeProps) {
  const [employeeId, setEmployeeId] = useState<number | null>(null);
  const [date, setDate] = useState(new Date());
  const [amount, setAmount] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [categoryName, setCategoryName] = useState<string>('');
  const [isCategoryListVisible, setCategoryListVisible] = useState(false);
  const [allCategory, setAllCategory] = useState<any[]>([]);
  const [category, setCategory] = useState<any[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number[]>([]);
  const [openCheckModal] = useState(false);
  const transformToHierarchyCallback = useCallback(transformToHierarchy, []);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get('category');
        setAllCategory(data);
        const hierarchy = transformToHierarchyCallback(data);
        setCategory(hierarchy);
      } catch {}
    };
    load();
  }, [transformToHierarchyCallback]);

  const handleNodeSelect = (nodeId: number) => {
    setSelectedCategoryId([nodeId]);
    setCategoryId(nodeId);
    const found = allCategory.find((c) => c.id === nodeId);
    if (found) setCategoryName(found.name || found.title || '');
    setCategoryListVisible(false);
  };

  const handleSave = async () => {
    if (!amount || !date) {
      enqueueSnackbar(t('FillRequired'), { variant: 'warning' });
      return;
    }
    try {
      await axios.post('finance-income/add', {
        amount: Number(amount),
        date,
        name: name || undefined,
        description: description || undefined,
        categoryId: categoryId || undefined,
        userId: employeeId ?? undefined,
      });
      enqueueSnackbar(t('SavedSuccessfully'), { variant: 'success' });
      refreshIncome();
      setShowModalAdd(false);
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Error', { variant: 'error' });
    }
  };


  return (
    <div className='fx-modal' role="presentation">
      {/* Header */}
      <div className='fx-head'>
        <div className='fx-head-text'>
          <div className='fx-head-title'>{t('AddIncome')}</div>
          <div className='fx-head-sub'>{t('AddIncomeSub')}</div>
        </div>
        <div className='fx-close' onClick={() => setShowModalAdd(false)}><ExitToAppIcon fontSize="small" /></div>
      </div>

      {/* Body */}
      <div className='fx-body'>
        {/* От кого + Супервайзер */}
        <ClaimantFields access={access} value={employeeId} onChange={setEmployeeId} />

        {/* Date + Amount */}
        <div className='fx-row2'>
          <div className='fx-field'>
            <CalendarPicker
              mode="single"
              variant="bare"
              className='fx-input'
              value={date}
              onChange={(d: Date) => setDate(d)}
              displayFormat="DD.MM.YYYY"
            />
          </div>
          <div className='fx-field'>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className='fx-input'
              placeholder={`${t('Amount')} *`}
            />
          </div>
        </div>

        {/* Category */}
        <div className='fx-field'>
          <CategoryTaskPicker
            nodes={category}
            selectedCategoryId={selectedCategoryId}
            categoryName={categoryName}
            onCategorySelect={(id, name) => {
              if (id === -1) { setCategoryId(null); setCategoryName(''); setSelectedCategoryId([]); }
              else { setCategoryId(id); setCategoryName(name); setSelectedCategoryId([id]); }
            }}
            selectedTasks={[]}
            setSelectedTasks={() => {}}
            taskOrTasks={false}
            categoryOnly
            block
          />
        </div>

        {/* Name */}
        <div className='fx-field'>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className='fx-input'
            placeholder={t('Name')}
          />
        </div>

        {/* Description */}
        <div className='fx-field'>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className='fx-textarea'
            placeholder={t('Description')}
          />
        </div>
      </div>

      {/* Footer */}
      <div className='fx-foot'>
        <button type="button" className='fx-save' onClick={handleSave}>
          {t('Save')}
        </button>
        <button type="button" className='fx-cancel' onClick={() => setShowModalAdd(false)}>
          {t('Cancel')}
        </button>
      </div>
    </div>
  );
}
